import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { AddCommentProps } from "@shared/components/EditorJs/comments/interfaces";
import { useUser } from "@services/UserContext";

const AddComment: React.FC<AddCommentProps> = ({
  selectedBlock,
  onCommentSubmit,
  setSelectedBlock,
  mousePosRef,
  selectedIds,
  setSelectedIds,
}) => {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [commentText, setCommentText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useUser();

  const updatePositions = () => {
    if (!selectedBlock && selectedIds.length === 0) return;
    if (!mousePosRef?.current) return;

    // Use viewport-based coordinates for portal-to-body + position: fixed
    const { clientX, clientY, x, y, pageX, pageY } = mousePosRef.current as any;
    const vx = clientX ?? x ?? pageX ?? 0;
    const vy = clientY ?? y ?? pageY ?? 0;

    const left = vx;
    const top = vy - 50;
    setPosition({ top, left });
  };

  useEffect(() => {
    updatePositions();
  }, [selectedBlock, selectedIds]);

  useEffect(() => {
    if (isInputVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isInputVisible]);

  const viewInput = () => {
    setIsInputVisible(false);
    let top = position?.top || 0;
    let left = position?.left || 0;

    const container = document.querySelector("#reportDiv");
    const containerRect = container?.getBoundingClientRect();
    if (containerRect) {
      const containerWidth = containerRect.width;
      const seventyPercentWidth = containerWidth * 0.8;

      if (left > seventyPercentWidth) {
        left = seventyPercentWidth - 100;
        if (left < 0) left = 120;
      }
      top = top + 10;
    }

    setPosition({ top, left });
    setIsInputVisible(true);
  };

  if (!position) return null;

  const commentBox = (
    <div
      className="z-10 px-3 py-1 rounded-lg flex flex-col items-center comment"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        position: "fixed",
      }}
    >
      {!isInputVisible ? (
        // Show Comment button
        <div
          className="flex items-center gap-2 px-2 py-1 bg-white comment border border-gray-300 shadow-md rounded-[0.2rem] cursor-pointer z-[99999]"
          onClick={viewInput}
        >
          <span className="comment">
            <img
              src="/icon_comment.svg"
              alt="Comment"
              className="w-5 h-5 comment"
            />
          </span>
          <span className="comment">Comment</span>
        </div>
      ) : (
        // Show input box for comment
        <div className="flex comment items-center gap-2 p-2 border border-gray-300 shadow-md rounded-lg bg-white z-[99999] mt-[-25px] ml-[-70px]">
          <div className="comment bg-red-500 text-white w-8 h-8 flex items-center justify-center rounded-full font-bold">
            {user?.first_name?.[0]}
          </div>
          <div className="items-center justify-center flex">
            <input
              ref={inputRef}
              type="text"
              className="border-none comment outline-none px-2 py-1 rounded-md w-[20rem]"
              placeholder="Type anything"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            <button
              className="comment text-white rounded-lg cursor-pointer"
              onClick={() => {
                if (commentText.trim() !== "") {
                  onCommentSubmit(commentText.trim());
                  setCommentText("");
                  setIsInputVisible(false);
                  setSelectedBlock(null);
                }
              }}
            >
              <img src="/send_icon.svg" alt="Comment" className="w-7 h-7" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Portal to body so it sits above all stacking contexts
  return createPortal(commentBox, document.body);
};

export default AddComment;
